'use client';
/* eslint-disable tailwindcss/no-arbitrary-value -- the tickets fix these in pixels (the 22px close icon, the 69/73px logo box, the 30px desktop logo) and no token carries them */

import { useEffect, useMemo, useState } from 'react';
import { CmsImage } from '../../../components/CmsImage';
import Close from '../../../components/icons/Close';
import { MaterialIcon } from '../../../components/MaterialIcon';
import { Modal } from '../../../components/Modal/Modal';
import type { CompareCandidate } from '../compareCandidates';
import { compareIdOf } from '../compareUrl';
import { compareImageOf, type CompareVariantLayout } from '../compareVariant';

export type ChoosePlansModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Plans the shopper may add; the ones already compared are excluded by the caller. */
  candidates: CompareCandidate[];
  /** The reader's pick; the caller loads the full plan. */
  onPick: (candidate: CompareCandidate) => void;
  /** The variant's title and search placeholder: plans or cards. */
  title: string;
  searchPlaceholder: string;
  /** The variant's no-match copy: a title and, for plans, a hint under it. */
  noMatch: { title: string; hint?: string };
  /** Whose picture a row shows, and whether the issuer is named and searchable. */
  layout: Pick<CompareVariantLayout, 'columnImage' | 'issuerLine'>;
};

/**
 * Type-ahead list of plans not yet compared. Filtering is a case-insensitive substring match on
 * the plan name (and the issuer, where the variant names it) from the first character; mixed EN /
 * ZH names match either way. Picking a plan closes the sheet.
 */
export const ChoosePlansModal = ({
  isOpen,
  onClose,
  candidates,
  onPick,
  title,
  searchPlaceholder,
  noMatch,
  layout,
}: ChoosePlansModalProps) => {
  // The list filters on every keystroke ("from the first character"), including inside an IME
  // composition: Android keyboards compose every Latin word, so waiting for the confirmation would
  // filter nothing until the word ends. What a composition must not do is flash "No Plans Found"
  // on pinyin or zhuyin letters that are not the name yet, so while composing an empty result keeps
  // the last list that had matches.
  const [query, setQuery] = useState('');
  const [composing, setComposing] = useState(false);
  const [held, setHeld] = useState<CompareCandidate[]>([]);
  const needle = query.trim().toLowerCase();

  const matches = useMemo(() => {
    // A candidate with no uid cannot be added (the selection is keyed by it), so it never lists.
    const addable = candidates.filter((product) => !!compareIdOf(product));
    // Where the issuer is named, it is searchable too: "HSBC" finds every HSBC card.
    const haystack = (product: CompareCandidate) =>
      [
        product.product_name ?? '',
        layout.issuerLine ? (product.provider?.provider_name ?? '') : '',
      ]
        .join(' ')
        .toLowerCase();
    return needle
      ? addable.filter((product) => haystack(product).includes(needle))
      : addable;
  }, [candidates, needle, layout.issuerLine]);

  useEffect(() => {
    if (matches.length > 0) setHeld(matches);
  }, [matches]);
  // The held list is only as current as the candidates: one no longer offered does not come back.
  const shown =
    composing && matches.length === 0
      ? held.filter((product) => candidates.includes(product))
      : matches;

  const close = () => {
    setQuery('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      sheetOnMobile
      hideCloseButton
      // The design draws this sheet taller than the shared one, with the card radius and no drag
      // handle (`cms.css`); its close button sits in the header row instead.
      // eslint-disable-next-line tailwindcss/no-custom-classname
      className="mhg-modal-sheet-tall p-0"
      data-component="compare-choose-plans"
    >
      {/* Title and search stay put while the list scrolls under them. */}
      <div className="flex h-full flex-col md:max-h-[90vh]">
        {/* From 1280 the dialog centres its title and pins the close button to the row's right edge. */}
        <div className="relative flex shrink-0 items-center gap-spacing/gap/x-sm border-b border-borders/default px-spacing/padding/x py-spacing/padding/y-sm lg:px-spacing/padding/x-sm lg:py-5">
          <h2 className="mhg-alpha-typography-subheading-2 flex-1 text-text/primary lg:text-center">
            {title}
          </h2>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="flex size-[22px] items-center justify-center text-text/primary lg:absolute lg:right-4 lg:size-6"
          >
            <Close className="size-[22px] lg:size-6" />
          </button>
        </div>
        <div className="shrink-0 px-spacing/gap/x py-spacing/padding/y-sm lg:px-5">
          <label className="flex h-9 items-center lg:h-10 gap-spacing/gap/x-xs rounded-radius/button border border-borders/form-default bg-buttons/base px-[--form-inner-element-padding]">
            <MaterialIcon
              icon="search"
              px={20}
              // 24 on desktop per the design; the inline size needs an important override.
              className="text-text/disabled lg:!text-[24px]"
            />
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onCompositionStart={() => setComposing(true)}
              onCompositionEnd={(event) => {
                setComposing(false);
                setQuery(event.currentTarget.value);
              }}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="w-full text-font-size/form-input font-font-weight/form-input leading-line-height/form-input bg-transparent text-text/primary outline-none placeholder:text-text/disabled"
            />
          </label>
        </div>
        {shown.length === 0 ? (
          <div
            className="flex flex-col items-center gap-spacing/gap/soft-12px px-spacing/gap/x py-spacing/gap/soft-12px text-center text-text/secondary-lighter"
            data-component="compare-no-plans"
          >
            <p className="mhg-alpha-typography-bullet-2">{noMatch.title}</p>
            {noMatch.hint && (
              <p className="mhg-alpha-typography-caption">{noMatch.hint}</p>
            )}
          </div>
        ) : (
          <ul className="min-h-0 flex-1 overflow-y-auto">
            {shown.map((product) => {
              const id = compareIdOf(product);
              const issuer = product.provider?.provider_name;
              const picture = compareImageOf(product, layout.columnImage);
              const logo = picture?.src;
              return (
                <li key={id}>
                  <button
                    type="button"
                    data-product-id={id}
                    onClick={() => {
                      onPick(product);
                      setQuery('');
                    }}
                    className="flex min-h-13 w-full items-center gap-spacing/gap/x-sm bg-fill/card px-spacing/gap/x py-spacing/gap/soft-12px text-left lg:gap-spacing/gap/x-xs lg:px-5 lg:py-2.5"
                  >
                    {/* The logo box keeps its width without a logo, so the names stay in one column. */}
                    <span className="flex w-[69px] shrink-0 items-center justify-center rounded-radius/button p-spacing/padding/soft-12px lg:w-[73px] lg:p-2.5">
                      {logo && (
                        <CmsImage
                          src={logo}
                          alt={
                            picture?.isProductImage
                              ? (product.product_name ?? '')
                              : (issuer ?? '')
                          }
                          width={53}
                          height={20}
                          sizes={{ _: 53 }}
                          className="h-5 w-full object-contain lg:h-[30px]"
                        />
                      )}
                    </span>
                    <span className="flex flex-1 flex-col">
                      <span className="mhg-alpha-typography-body-2 text-text/primary">
                        {product.product_name}
                      </span>
                      {layout.issuerLine && issuer && (
                        <span className="mhg-alpha-typography-caption text-text/secondary-lighter">
                          {issuer}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
};
